/**
 * @fileoverview Issue schemas for newsletter issue validation and type definitions
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

// Base Issue schema without defaults (for partial updates)
const BaseIssueSchema = z.object({
  id: z
    .string()
    .regex(/^\d{5}$/)
    .openapi({
      description: 'Zero-padded 5-digit issue number used as unique identifier',
      example: '00042',
    }),
  issueNumber: z.number().int().positive().openapi({
    description: 'Sequential issue number',
    example: 42,
  }),
  title: z.string().openapi({
    description: 'The title of the newsletter issue',
    example: 'Weekly Newsletter #42',
  }),
  description: z.string().openapi({
    description: 'Brief description of the issue',
    example: 'This week in tech: AI developments and startup news',
  }),
  status: z.enum(['published', 'unpublished']).openapi({
    description: 'Publication status of the issue',
    example: 'published',
  }),
  sponsors: z.array(z.string()).openapi({
    description: 'Array of sponsor IDs',
    example: ['sponsor-123', 'sponsor-456'],
  }),
  items: z
    .array(
      z.object({
        category: z.string().openapi({
          description: 'Category/section name',
          example: 'news',
        }),
        items: z.array(z.string()).openapi({
          description: 'Ordered array of item instance IDs in this category',
          example: ['hash1:ulid1', 'hash2:ulid2'],
        }),
        simpleAttribution: z.boolean().optional().openapi({
          description: 'Whether to use simple attribution format (author on same line as title)',
          example: false,
        }),
      })
    )
    .openapi({
      description: 'Ordered items grouped by category/section',
      example: [
        { category: 'news', items: ['hash1:ulid1', 'hash2:ulid2'] },
        { category: 'tutorial', items: ['hash3:ulid3'], simpleAttribution: false },
      ],
    }),
  issueDate: z.string().optional().openapi({
    description: 'The date the newsletter issue covers (YYYY-MM-DD)',
    example: '2024-01-15',
  }),
  publishedAt: z.string().datetime().optional().openapi({
    description: 'ISO timestamp when the issue was published',
    example: '2024-01-15T10:30:00Z',
  }),
  scheduledFor: z.string().datetime().optional().openapi({
    description: 'ISO timestamp for scheduled publishing',
    example: '2024-01-20T09:00:00Z',
  }),
  authorId: z.string().uuid().openapi({
    description: 'The ID of the user who created/edited the issue',
    example: '550e8400-e29b-41d4-a716-446655440001',
  }),
  authorName: z.string().openapi({
    description: 'The display name of the issue author/editor',
    example: 'Jane Editor',
  }),
  subject: z.string().optional().openapi({
    description: 'Email subject line (might differ from title)',
    example: '📰 Your Weekly Tech Update #42',
  }),
  previewText: z.string().optional().openapi({
    description: 'Email preview/preheader text',
    example: 'AI breakthroughs, startup funding, and more inside...',
  }),
  sentCount: z.number().int().min(0).openapi({
    description: 'Number of email requests sent',
    example: 5000,
  }),
  deliveredCount: z.number().int().min(0).openapi({
    description: 'Number of emails successfully delivered',
    example: 4900,
  }),
  openCount: z.number().int().min(0).openapi({
    description: 'Total number of opens',
    example: 2500,
  }),
  uniqueOpenCount: z.number().int().min(0).openapi({
    description: 'Number of unique opens',
    example: 1250,
  }),
  clickCount: z.number().int().min(0).openapi({
    description: 'Total number of clicks',
    example: 500,
  }),
  uniqueClickCount: z.number().int().min(0).openapi({
    description: 'Number of unique clicks',
    example: 250,
  }),
  bounceCount: z.number().int().min(0).openapi({
    description: 'Number of bounces',
    example: 15,
  }),
  bounceDropCount: z.number().int().min(0).openapi({
    description: 'Number of emails dropped due to prior bounce',
    example: 5,
  }),
  invalidEmailCount: z.number().int().min(0).openapi({
    description: 'Number of invalid email addresses',
    example: 3,
  }),
  unsubscribeCount: z.number().int().min(0).openapi({
    description: 'Number of unsubscribes from this issue',
    example: 12,
  }),
  spamReportCount: z.number().int().min(0).openapi({
    description: 'Number of spam reports',
    example: 2,
  }),
  spamReportDropCount: z.number().int().min(0).openapi({
    description: 'Number of emails dropped due to prior spam report',
    example: 1,
  }),
  deliveryRate: z.number().min(0).max(100).openapi({
    description: 'Percentage successfully delivered',
    example: 98.5,
  }),
  tags: z.array(z.string()).openapi({
    description: 'Array of topic tags for categorization',
    example: ['tech', 'startups', 'AI'],
  }),
  notes: z.string().openapi({
    description: 'Internal notes about the issue',
    example: 'Remember to include sponsor spotlight',
  }),
  singleSendId: z.string().optional().openapi({
    description: 'SendGrid Single Send campaign ID linked to this issue',
    example: 'abc123-def456-ghi789',
  }),
  content: z.string().openapi({
    description: 'Markdown content of the issue',
    example: '# Welcome to Issue #42\n\nThis week we cover...',
  }),
  created: z.iso.datetime().openapi({
    description: 'ISO 8601 timestamp when the issue was created',
    example: '2024-01-15T10:30:00Z',
  }),
  modified: z.iso.datetime().openapi({
    description: 'ISO 8601 timestamp when the issue was last updated',
    example: '2024-01-15T14:45:00Z',
  }),
});

// Full Issue schema with defaults (for creation and storage)
export const IssueSchema = BaseIssueSchema.extend({
  status: BaseIssueSchema.shape.status.default('unpublished'),
  sponsors: BaseIssueSchema.shape.sponsors.default([]),
  items: BaseIssueSchema.shape.items.default([]),
  sentCount: BaseIssueSchema.shape.sentCount.default(0),
  deliveredCount: BaseIssueSchema.shape.deliveredCount.default(0),
  openCount: BaseIssueSchema.shape.openCount.default(0),
  uniqueOpenCount: BaseIssueSchema.shape.uniqueOpenCount.default(0),
  clickCount: BaseIssueSchema.shape.clickCount.default(0),
  uniqueClickCount: BaseIssueSchema.shape.uniqueClickCount.default(0),
  bounceCount: BaseIssueSchema.shape.bounceCount.default(0),
  bounceDropCount: BaseIssueSchema.shape.bounceDropCount.default(0),
  invalidEmailCount: BaseIssueSchema.shape.invalidEmailCount.default(0),
  unsubscribeCount: BaseIssueSchema.shape.unsubscribeCount.default(0),
  spamReportCount: BaseIssueSchema.shape.spamReportCount.default(0),
  spamReportDropCount: BaseIssueSchema.shape.spamReportDropCount.default(0),
  deliveryRate: BaseIssueSchema.shape.deliveryRate.default(0),
  tags: BaseIssueSchema.shape.tags.default([]),
  notes: BaseIssueSchema.shape.notes.default(''),
  content: BaseIssueSchema.shape.content.default(''),
}).openapi('Issue');

export const CreateIssueSchema = IssueSchema.omit({
  id: true,
  created: true,
  modified: true,
}).extend({
  issueNumber: z.number().int().positive().optional(),
}).openapi('CreateIssue');

export const UpdateIssueSchema = BaseIssueSchema.partial()
  .omit({ id: true, created: true, modified: true, authorId: true, authorName: true })
  .openapi('UpdateIssue');

export const IssueListResponseSchema = createListResponseSchema(
  IssueSchema,
  'issues',
  'IssueListResponse'
);
